<template>
  <div class="vuuro-onboarding" ref="root" @dragover="onCardDragOver" @drop="onCardDrop">
    <div v-if="done" class="vuuro-onboarding__panel" role="status">
      <h2 class="vuuro-onboarding__title">{{ doneTitle }}</h2>
      <!-- The thank-you as short points (Mark, 16 Oct); the one text of a server from before the list otherwise. -->
      <ul v-if="doneBullets.length" class="vuuro-onboarding__bullets">
        <li v-for="(bullet, index) in doneBullets" :key="index">{{ bullet }}</li>
      </ul>
      <p v-else class="vuuro-onboarding__text">{{ doneMessage }}</p>
      <p v-if="donePreviewUrl" class="vuuro-onboarding__preview">
        <a :href="donePreviewUrl" class="vuuro-onboarding__preview-link" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer">{{ t.preview_link }}</a>
        <span class="vuuro-onboarding__preview-note">{{ t.preview_disclaimer }}</span>
      </p>
    </div>

    <div v-else-if="expired" class="vuuro-onboarding__panel" role="alert">
      <p class="vuuro-onboarding__text">{{ t.session_expired }}</p>
      <button type="button" class="theme-btn theme-btn--primary vuuro-onboarding__button" @click="reload">{{ t.reload }}</button>
    </div>

    <form v-else class="vuuro-onboarding__form" novalidate @submit.prevent="submit">
      <div class="vuuro-onboarding__head">
        <h2 class="vuuro-onboarding__title">{{ t.title }}</h2>

        <ol class="vuuro-onboarding__steps" :aria-label="t.steps_label">
          <li
            v-for="(label, index) in stepLabels"
            :key="'step-label-' + index"
            class="vuuro-onboarding__step"
            :class="{ 'is-active': step === index + 1, 'is-done': step > index + 1 }"
            :aria-current="step === index + 1 ? 'step' : null"
          >
            <span class="vuuro-onboarding__step-nr">{{ index + 1 }}</span>
            <span class="vuuro-onboarding__step-label">{{ label }}</span>
          </li>
        </ol>
      </div>
      <p class="vuuro-onboarding__counter" ref="counter" tabindex="-1">{{ counterText }}</p>

      <fieldset v-show="step === 1" class="vuuro-onboarding__fieldset">
        <legend class="vuuro-onboarding__legend">{{ stepLabels[0] }}</legend>
        <p class="vuuro-onboarding__text">{{ t.property_intro }}</p>
        <!-- Step 1 in three parts: the address (postcode, house number, addition; looked up while
             typing), a summary card with the address found and the floor area, year built and energy
             label that came with it, then the other property fields. Street and city are edited in
             the card only when the guest asks, the lookup is unavailable or they are missing. -->
        <div v-for="section in step1Sections" :key="section.name" :class="'vuuro-onboarding__' + section.name">
          <p v-if="section.name === 'address'" class="vuuro-onboarding__hint vuuro-onboarding__address-hint">{{ t.address_hint }}</p>
          <template v-if="section.name === 'address-summary'">
            <p class="vuuro-onboarding__address-title">{{ t.address_summary_title }}</p>
            <div v-if="hasAddress && !showPlaceFields" class="vuuro-onboarding__address-head">
              <p class="vuuro-onboarding__address-line">{{ addressLines[0] }}<br>{{ addressLines[1] }}</p>
              <button type="button" class="vuuro-onboarding__address-link" data-address-no-commit @mousedown.left="onAddressTakeOver" @click="editAddress">{{ t.address_edit }}</button>
            </div>
            <p v-if="energyStatus === 'loading'" class="vuuro-onboarding__hint" role="status">{{ t.address_enrichment_loading }}</p>
          </template>
          <div class="vuuro-onboarding__grid" :class="'vuuro-onboarding__grid--' + section.name">
            <div
              v-for="field in section.fields"
              :key="field.name"
              class="vuuro-onboarding__field"
              :class="{ 'vuuro-onboarding__field--wide': field.wide, 'has-error': errors[field.name] }"
            >
              <label class="vuuro-onboarding__label" :for="fieldId(field.name)">
                {{ t[field.label] }}<span v-if="field.required" aria-hidden="true"> *</span>
              </label>
              <select
                v-if="field.type === 'select'"
                :id="fieldId(field.name)"
                v-model="form[field.name]"
                class="vuuro-onboarding__input"
                :required="field.required"
                :aria-invalid="errors[field.name] ? 'true' : 'false'"
                :aria-describedby="errors[field.name] ? errorId(field.name) : null"
                @change="clearError(field.name)"
              >
                <option value="">{{ t.choose }}</option>
                <option v-for="type in config.propertyTypes" :key="type.id" :value="String(type.id)">{{ type.label }}</option>
              </select>
              <!-- name only for the address typeahead, whose arrow keys work in fields named like add-house's. -->
              <input
                v-else
                :id="fieldId(field.name)"
                v-model.trim="form[field.name]"
                class="vuuro-onboarding__input"
                :name="field.inputName || null"
                :type="field.type"
                :inputmode="field.inputmode || null"
                :autocomplete="field.autocomplete || 'off'"
                :placeholder="field.placeholder ? t[field.placeholder] : null"
                :min="field.min"
                :max="field.max"
                :step="field.step || null"
                :maxlength="field.maxlength || null"
                :required="field.required"
                :aria-invalid="errors[field.name] ? 'true' : 'false'"
                :aria-describedby="errors[field.name] ? errorId(field.name) : null"
                @input="onFieldInput(field.name)"
                @keydown="onFieldKeydown(field.name, $event)"
                @blur="onFieldBlur(field.name)"
              >
              <p v-if="errors[field.name]" :id="errorId(field.name)" class="vuuro-onboarding__error">{{ errorText(errors[field.name]) }}</p>
            </div>
            <!-- The area hint is a row of its own (full width), so it does not leave a gap under the year. -->
            <p v-if="section.name === 'address-summary' && areaFromLookup" class="vuuro-onboarding__hint vuuro-onboarding__area-hint">{{ t.area_hint }}</p>
            <!-- The energy label is shown, not chosen: it comes with the address and the office checks it. -->
            <div v-if="section.name === 'address-summary'" class="vuuro-onboarding__field vuuro-onboarding__energy">
              <span class="vuuro-onboarding__label">{{ t.energy_label_display }}</span>
              <span v-if="energyStatus === 'loading' || energyLabelText || !energyNote" class="vuuro-onboarding__energy-value">{{ energyStatus === 'loading' ? '...' : (energyLabelText || '-') }}</span>
              <span v-if="energyNote" class="vuuro-onboarding__hint vuuro-onboarding__energy-note">{{ energyNote }}</span>
            </div>
          </div>
          <!-- A tap or click on a suggestion is the guest's own choice, also when a resumed form opened the list by itself. -->
          <div v-if="section.name === 'address'" class="vuuro-onboarding__lookup" @mousedown.capture="chooseAddress" @touchstart.capture="chooseAddress">
            <pdok-address-typeahead
              v-if="config.urls.address_suggest"
              ref="lookup"
              :postcode="form.postcode"
              :house-number="form.house_number"
              :addition="form.house_number_addition"
              :suggest-url="config.urls.address_suggest"
              :resolve-url="config.urls.address_resolve"
              :suggest-postcode-url="config.urls.address_suggest_postcode"
              :resolve-postcode-url="config.urls.address_resolve_postcode"
              :locale="config.lang === 'en' ? 'en' : 'nl'"
              :commit-on-blur="true"
              :quiet="quietLookup"
              :address-filled="hasAddress"
              @resolved="onAddressResolved"
              @resolved-postcode="onPostcodeResolved"
              @unavailable="onAddressUnavailable"
              @settling="onAddressSettling"
            ></pdok-address-typeahead>
          </div>
          <button v-if="section.name === 'address' && !summaryVisible" type="button" class="vuuro-onboarding__address-link vuuro-onboarding__address-manual" data-address-no-commit @mousedown.left="onAddressTakeOver" @click="editAddress">{{ t.address_manual }}</button>
        </div>

        <!-- Photos also by dragging them onto this section (desktop); same checks and upload as the button. -->
        <div
          ref="photoSection"
          class="vuuro-onboarding__photos"
          :class="{ 'is-dragover': dragOver }"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent="onDragOver"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
        >
          <div class="vuuro-onboarding__photos-head">
            <p class="vuuro-onboarding__label">{{ t.photos }}</p>
            <button
              v-if="photos.length"
              ref="removeAll"
              type="button"
              class="vuuro-onboarding__photos-remove-all"
              :disabled="!canRemoveAll"
              @click="removeAllPhotos"
            >{{ t.photos_remove_all }}</button>
          </div>
          <p class="vuuro-onboarding__hint">{{ t.photos_hint }}</p>
          <ul v-if="photos.length" class="vuuro-onboarding__gallery">
            <li v-for="photo in photos" :key="photo.key" class="vuuro-onboarding__tile" :class="'is-' + photo.status">
              <div class="vuuro-onboarding__tile-media">
                <button
                  v-if="canOpen(photo)"
                  type="button"
                  class="vuuro-onboarding__tile-open"
                  :aria-label="t.photo_open + ': ' + photo.name"
                  @click="openPhoto(photo)"
                ><img :src="photo.preview" alt="" class="vuuro-onboarding__tile-img" decoding="async" loading="lazy" @error="previewFailed(photo)"></button>
                <img v-else-if="photo.preview" :src="photo.preview" alt="" class="vuuro-onboarding__tile-img" decoding="async" loading="lazy" @error="previewFailed(photo)">
                <span v-else class="vuuro-onboarding__tile-empty" aria-hidden="true"></span>
                <span v-if="photo.status === 'uploading'" class="vuuro-onboarding__tile-badge">{{ t.photo_uploading_short }} {{ photo.progress }}%</span>
                <span v-else-if="photo.status === 'failed'" class="vuuro-onboarding__tile-badge">{{ t.photo_failed_short }}</span>
                <span v-else-if="!photo.preview && !photo.previewing" class="vuuro-onboarding__tile-badge">{{ t.photo_no_preview }}</span>
                <button
                  type="button"
                  class="vuuro-onboarding__tile-remove"
                  :disabled="photo.removing || photo.status === 'uploading' || sending || bulkRemoving"
                  :aria-label="t.photo_remove + ': ' + photo.name"
                  @click="removePhoto(photo)"
                ><span aria-hidden="true">&times;</span></button>
              </div>
              <span
                v-if="photo.status === 'uploading'"
                class="vuuro-onboarding__tile-progress"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="photo.progress"
                :aria-label="t.photo_uploading_short + ': ' + photo.name"
              ><span class="vuuro-onboarding__tile-progress-fill" :style="{ width: photo.progress + '%' }"></span></span>
              <span class="vuuro-onboarding__tile-name">{{ photo.name }}</span>
              <span v-if="photo.status === 'failed'" class="vuuro-onboarding__tile-error" role="alert">{{ photo.error }}</span>
            </li>
          </ul>
          <p v-if="photoError" class="vuuro-onboarding__error" role="alert">{{ photoError }}</p>
          <!-- The pick and drop zone: a click or a tap opens the picker, a drag lands on the whole section. -->
          <label class="vuuro-onboarding__dropzone" :class="{ 'is-busy': uploading, 'is-disabled': pickDisabled }">
            <input ref="photoInput" type="file" accept="image/jpeg,image/png,image/gif,image/heic,image/heif,.heic,.heif" multiple class="vuuro-onboarding__file" :disabled="pickDisabled" @change="addPhotos">
            <span class="vuuro-onboarding__dropzone-icon" aria-hidden="true">+</span>
            <span class="vuuro-onboarding__dropzone-title">{{ uploading ? t.photo_uploading : t.photos_add }}</span>
            <span class="vuuro-onboarding__dropzone-hint vuuro-onboarding__dropzone-hint--pointer">{{ t.photos_drop }}</span>
            <span class="vuuro-onboarding__dropzone-hint vuuro-onboarding__dropzone-hint--touch">{{ t.photos_tap }}</span>
          </label>
        </div>
      </fieldset>

      <fieldset v-show="step === 2" class="vuuro-onboarding__fieldset">
        <legend class="vuuro-onboarding__legend">{{ stepLabels[1] }}</legend>

        <div class="vuuro-onboarding__block">
          <h3 class="vuuro-onboarding__subtitle">{{ t.viewing_title }}</h3>
          <p class="vuuro-onboarding__text">{{ t.viewing_intro }}</p>
          <!-- One row per planned moment (at least one, at most maxSlots), each with its own maximum. -->
          <div v-if="!form.viewing_skipped" class="vuuro-onboarding__slots">
            <div v-for="(slot, index) in form.viewing_slots" :key="slot.key" class="vuuro-onboarding__slot">
              <div class="vuuro-onboarding__slot-head">
                <h4 class="vuuro-onboarding__slot-title">{{ slotHeading(index) }}</h4>
                <button
                  type="button"
                  class="vuuro-onboarding__photo-remove vuuro-onboarding__slot-remove"
                  :disabled="form.viewing_slots.length === 1 || sending"
                  :aria-label="t.viewing_remove_slot + ': ' + slotHeading(index)"
                  @click="removeSlot(index)"
                >{{ t.viewing_remove_slot }}</button>
              </div>
              <div class="vuuro-onboarding__grid vuuro-onboarding__grid--slot">
                <div
                  v-for="field in slotFields"
                  :key="field.name"
                  class="vuuro-onboarding__field"
                  :class="{ 'has-error': errors[slotKey(index, field.name)] }"
                >
                  <label class="vuuro-onboarding__label" :for="slotId(index, field.name)">
                    {{ t[field.label] }}<span aria-hidden="true"> *</span>
                  </label>
                  <!-- The time as two lists, hours and five-minute steps: a native time picker shows
                       every minute on desktop Chrome and iOS whatever its step attribute says. -->
                  <div v-if="field.type === 'time-select'" class="vuuro-onboarding__time">
                    <select
                      :id="slotId(index, field.name)"
                      class="vuuro-onboarding__input"
                      :value="timePart(slot.time, 0)"
                      required
                      :aria-label="t.viewing_time + ': ' + t.viewing_hour"
                      :aria-invalid="errors[slotKey(index, field.name)] ? 'true' : 'false'"
                      :aria-describedby="errors[slotKey(index, field.name)] ? errorId(slotId(index, field.name)) : null"
                      @change="setTime(slot, index, 0, $event.target.value)"
                    >
                      <option value="">--</option>
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                    <span class="vuuro-onboarding__time-sep" aria-hidden="true">:</span>
                    <select
                      :id="slotId(index, 'minute')"
                      class="vuuro-onboarding__input"
                      :value="timePart(slot.time, 1)"
                      required
                      :aria-label="t.viewing_time + ': ' + t.viewing_minute"
                      :aria-invalid="errors[slotKey(index, field.name)] ? 'true' : 'false'"
                      :aria-describedby="errors[slotKey(index, field.name)] ? errorId(slotId(index, field.name)) : null"
                      @change="setTime(slot, index, 1, $event.target.value)"
                    >
                      <option value="">--</option>
                      <option v-for="minute in minutes" :key="minute" :value="minute">{{ minute }}</option>
                    </select>
                  </div>
                  <input
                    v-else
                    :id="slotId(index, field.name)"
                    v-model.trim="slot[field.name]"
                    class="vuuro-onboarding__input"
                    :type="field.type"
                    :inputmode="field.inputmode || null"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step || null"
                    required
                    :aria-invalid="errors[slotKey(index, field.name)] ? 'true' : 'false'"
                    :aria-describedby="errors[slotKey(index, field.name)] ? errorId(slotId(index, field.name)) : null"
                    @input="clearError(slotKey(index, field.name))"
                    @blur="saveStateNow"
                  >
                  <p v-if="errors[slotKey(index, field.name)]" :id="errorId(slotId(index, field.name))" class="vuuro-onboarding__error">{{ errorText(errors[slotKey(index, field.name)]) }}</p>
                </div>
              </div>
            </div>
            <p v-if="errors.viewing_slots" class="vuuro-onboarding__error" role="alert">{{ errorText(errors.viewing_slots) }}</p>
            <button type="button" class="theme-btn theme-btn--text vuuro-onboarding__slot-add" :disabled="sending" @click="addSlot">{{ t.viewing_add_slot }}</button>
          </div>
          <!-- The second way, under the fields it replaces: no date yet, the office plans the viewings with the landlord. -->
          <p v-if="!form.viewing_skipped" class="vuuro-onboarding__or">{{ t.viewing_or }}</p>
          <label class="vuuro-onboarding__check" :for="fieldId('viewing_skipped')">
            <input :id="fieldId('viewing_skipped')" v-model="form.viewing_skipped" type="checkbox" @change="toggleSkipped">
            <span>{{ t.viewing_skip }}</span>
          </label>
          <!-- The preview with the planned moments as not final (Mark, 16 Oct): secondary, in a new tab. The
               paragraph keeps its place under the moments, so the form does not jump while the guest edits them
               (19 Oct). The link has its address only once the server took a save of exactly the moments on
               screen, with at least one moment still to come; until then the same words stand muted, without an
               address, so the new tab shows the moments this tab saved (serverSlotsKey says what it cannot see).
               A plain link, so no popup blocker stops it. -->
          <p v-if="propertyPreviewUrl && !form.viewing_skipped" class="vuuro-onboarding__preview">
            <a v-if="step2PreviewReady" :href="propertyPreviewUrl" class="vuuro-onboarding__preview-link" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer">{{ t.preview_link }}</a>
            <span v-else class="vuuro-onboarding__preview-wait" role="link" aria-disabled="true">{{ t.preview_link }}</span>
            <span class="vuuro-onboarding__preview-note">{{ t.preview_disclaimer_step2 }}</span>
          </p>
        </div>

        <div class="vuuro-onboarding__block">
          <h3 class="vuuro-onboarding__subtitle">{{ t.contact_intro }}</h3>
          <!-- Above the fields, read before typing: the office may contact the guest with what is typed here,
               also when the form is not sent (Mark, 15 Oct: TONEN-CONCEPT-CONTACT). -->
          <p :id="fieldId('contact_note')" class="vuuro-onboarding__hint vuuro-onboarding__contact-note">{{ t.contact_note }}</p>
          <div class="vuuro-onboarding__grid vuuro-onboarding__grid--pairs">
            <div
              v-for="field in fieldsOf('contact')"
              :key="field.name"
              class="vuuro-onboarding__field"
              :class="{ 'vuuro-onboarding__field--wide': field.wide, 'has-error': errors[field.name] }"
            >
              <label class="vuuro-onboarding__label" :for="fieldId(field.name)">
                {{ t[field.label] }}<span aria-hidden="true"> *</span>
              </label>
              <input
                :id="fieldId(field.name)"
                v-model.trim="form[field.name]"
                class="vuuro-onboarding__input"
                :type="field.type"
                :autocomplete="field.autocomplete || 'off'"
                :maxlength="field.maxlength || null"
                required
                :aria-invalid="errors[field.name] ? 'true' : 'false'"
                :aria-describedby="errors[field.name] ? fieldId('contact_note') + ' ' + errorId(field.name) : fieldId('contact_note')"
                @input="clearError(field.name)"
                @blur="saveStateNow"
              >
              <p v-if="errors[field.name]" :id="errorId(field.name)" class="vuuro-onboarding__error">{{ errorText(errors[field.name]) }}</p>
            </div>
          </div>
        </div>
      </fieldset>

      <div class="vuuro-onboarding__honeypot" aria-hidden="true">
        <label :for="fieldId('website_url')">Website</label>
        <input :id="fieldId('website_url')" v-model="honeypot" type="text" tabindex="-1" autocomplete="off">
      </div>

      <div class="vuuro-onboarding__cta-rail">
        <p v-if="formError" class="vuuro-onboarding__error vuuro-onboarding__error--form" role="alert">{{ formError }}</p>

        <div class="vuuro-onboarding__nav">
          <button v-if="step === 2" type="button" class="theme-btn theme-btn--text vuuro-onboarding__back" :disabled="busy" @click="goTo(1)">{{ t.back }}</button>
          <button v-if="step === 1" key="next" type="button" class="theme-btn theme-btn--primary vuuro-onboarding__next" :disabled="busy" @mousedown.left="onNextPress" @click="saveProperty">
            {{ sending ? t.saving : t.next_property }}
          </button>
          <button v-else key="submit" type="submit" class="theme-btn theme-btn--primary vuuro-onboarding__next" :disabled="busy">
            {{ sending ? t.sending : t.submit }}
          </button>
        </div>
      </div>
      <!-- Under the rail, in the flow of the page: on a phone only the buttons stay in view. -->
      <p v-if="step === 1" class="vuuro-onboarding__hint vuuro-onboarding__promise">{{ t.save_promise }}</p>
      <!-- Once step 1 is saved in this page: the preview of the property page, without moments yet. -->
      <p v-if="step === 1 && propertyPreviewUrl" class="vuuro-onboarding__preview">
        <a :href="propertyPreviewUrl" class="vuuro-onboarding__preview-link" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer">{{ t.preview_link }}</a>
        <span class="vuuro-onboarding__preview-note">{{ t.preview_disclaimer_step1 }}</span>
      </p>
      <!-- A form this browser picked up again (its cookie, 14 days): a shared device can let go of it. -->
      <p v-if="step === 1 && config.resumed" class="vuuro-onboarding__restart-row">
        <button type="button" class="theme-btn theme-btn--text vuuro-onboarding__restart" :disabled="busy || restarting" @click="startAgain">{{ t.restart }}</button>
      </p>
    </form>
  </div>
</template>

<script>
import { buildFancyboxItems, openPropertyGallery } from '../lib/fancyboxPropertyGallery';

// Step 1 groups, in screen order: the address row, street and city (edited in the summary card when
// needed), the floor area and year built (in the card), the other property fields.
const FIELDS = [
  { group: 'address', name: 'postcode', label: 'postcode', type: 'text', required: true, autocomplete: 'postal-code', maxlength: 12, inputName: 'postcode', placeholder: 'postcode_placeholder' },
  { group: 'address', name: 'house_number', label: 'house_number', type: 'text', required: true, maxlength: 20, inputName: 'building_uid', placeholder: 'house_number_placeholder' },
  { group: 'address', name: 'house_number_addition', label: 'house_number_addition', type: 'text', maxlength: 20, inputName: 'building_uid_appendix' },
  { group: 'place', name: 'street', label: 'street', type: 'text', required: true, wide: true, autocomplete: 'address-line1', maxlength: 255 },
  { group: 'place', name: 'city', label: 'city', type: 'text', required: true, wide: true, autocomplete: 'address-level2', maxlength: 100 },
  { group: 'facts', name: 'area_m2', label: 'area', type: 'number', required: true, inputmode: 'numeric', min: 1, max: 999999, integer: true },
  { group: 'facts', name: 'construction_year', label: 'construction_year', type: 'number', inputmode: 'numeric', min: 1000, max: 2100, integer: true },
  // In pairs at every width: type and bedrooms, rent and available from.
  { group: 'property', name: 'property_type_id', label: 'property_type', type: 'select', required: true },
  { group: 'property', name: 'bedrooms', label: 'bedrooms', type: 'number', required: true, inputmode: 'numeric', min: 0, max: 20, integer: true },
  { group: 'property', name: 'price', label: 'price', type: 'number', required: true, inputmode: 'numeric', min: 1, max: 999999, integer: true },
  { group: 'property', name: 'available_from', label: 'available_from', type: 'date', required: true },
  { group: 'contact', name: 'first_name', label: 'first_name', type: 'text', required: true, autocomplete: 'given-name', maxlength: 100 },
  { group: 'contact', name: 'last_name', label: 'last_name', type: 'text', required: true, autocomplete: 'family-name', maxlength: 100 },
  { group: 'contact', name: 'phone_number', label: 'phone_number', type: 'tel', required: true, wide: true, autocomplete: 'tel', maxlength: 30 },
  { group: 'contact', name: 'email', label: 'email', type: 'email', required: true, autocomplete: 'email', maxlength: 255, wide: true },
];

// The fields of one planned viewing moment; step 2 has one row of these per moment (viewing_slots).
const SLOT_FIELDS = [
  { name: 'date', label: 'viewing_date', type: 'date' },
  { name: 'time', label: 'viewing_time', type: 'time-select' },
  { name: 'max_people', label: 'max_people', type: 'number', inputmode: 'numeric', min: 1, max: 100, integer: true },
];
// The hours a viewing can start (Mark, 15 Oct: 07 to 22) and the minutes in steps of five, which the
// server checks too (viewing_time_step).
const HOURS = Array.from({ length: 16 }, (unused, index) => String(index + 7).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (unused, index) => String(index * 5).padStart(2, '0'));
// Typing in these fields asks the typeahead for a new address.
const LOOKUP_FIELDS = ['postcode', 'house_number', 'house_number_addition'];

const STEP_GROUPS = { 1: ['address', 'place', 'facts', 'property'], 2: ['contact'] };
let slotSeq = 0;
const PHOTO_UPLOADS_AT_ONCE = 3;
// The photos the wizard takes, as the file input's accept list says (a drop skips that list).
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'heif'];
// An upload fails when it takes longer than five minutes plus the time a slow line (16 KB/s, a phone
// uplink shared by three uploads) needs for its bytes: a stalled request would hold the tile and step 1.
const PHOTO_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
const PHOTO_MIN_BYTES_PER_MS = 16;
// A delete (one photo or all) with no answer after a minute fails, so a stalled request never holds
// the photos and the step 1 button; trying again is safe (the server answers absent or changed).
const PHOTO_DELETE_TIMEOUT_MS = 60 * 1000;
// Retries after a 429 (more than 30 photos in a minute), each after the server's Retry-After.
const PHOTO_RETRIES_ON_429 = 3;
// A tile shows a copy with this short side, made once per photo added in this tab, so the grid does not
// hold camera originals for tiles of about 100 CSS px (VUURO-15). On a phone of 376 to about 410 CSS px the
// grid has three columns of 96 to about 103 CSS px, up to about 310 device px at three per CSS px; 320 covers
// that without enlarging (192 measured soft). At 430 CSS px it is enlarged about 1.07 times; below 376 CSS px
// the grid has two columns of about 138 CSS px and it is enlarged about 1.3 times.
const TILE_PREVIEW_PX = 320;
// The next photo waits up to this long for a copy (decode, draw and encode); a copy that takes longer gives
// way to the file itself, so it never holds the photos after it. A decode that hangs cannot be stopped and
// runs on beside the next one, but is not drawn when it lands.
const PREVIEW_DECODE_MS = 30000;
// Upload progress repaints a tile at most this often (about four times a second).
const PROGRESS_PAINT_MS = 250;

// Upright as the browser shows the photo; an engine that does not know that option gets the plain call.
function decodeBitmap(file) {
  try {
    return window.createImageBitmap(file, { imageOrientation: 'from-image' })
      .catch((error) => (error && error.name === 'TypeError' ? window.createImageBitmap(file) : Promise.reject(error)));
  } catch (error) {
    return window.createImageBitmap(file);
  }
}

// The photo decoded once and drawn with its short side at TILE_PREVIEW_PX as a JPEG on white (a transparent
// PNG would turn black); rejects where the browser cannot decode the file, has no canvas, or takes longer
// than PREVIEW_DECODE_MS.
function smallPreviewBlob(file) {
  if (typeof window.createImageBitmap !== 'function') return Promise.reject(new Error('no createImageBitmap'));
  // The canvas goes to 0x0 on every way out: drawn, failed, no canvas here, or the time limit first (an
  // encoder that never calls back would otherwise keep it at full size).
  let canvas = null;
  let gaveUp = false;
  const release = () => {
    if (!canvas) return;
    canvas.width = 0;
    canvas.height = 0;
  };
  // A decode that throws at once rejects like one that fails later.
  const copy = Promise.resolve().then(() => decodeBitmap(file)).then((bitmap) => new Promise((resolve, reject) => {
    try {
      if (gaveUp) throw new Error('copy took too long');
      const scale = Math.min(1, TILE_PREVIEW_PX / Math.max(1, Math.min(bitmap.width, bitmap.height)));
      canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext ? canvas.getContext('2d') : null;
      if (!context || typeof canvas.toBlob !== 'function') throw new Error('no canvas');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        release();
        if (blob) resolve(blob); else reject(new Error('no blob'));
      }, 'image/jpeg', 0.82);
    } catch (error) {
      release();
      reject(error);
    } finally {
      if (bitmap.close) bitmap.close();
    }
  }));
  let timer;
  const late = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      gaveUp = true;
      release();
      reject(new Error('copy took too long'));
    }, PREVIEW_DECODE_MS);
  });
  return Promise.race([copy, late]).finally(() => clearTimeout(timer));
}
const STATE_DELAY_MS = 1500;
// The labels of the Fancybox viewer (it ships only English and German) come from the texts, per key.
const VIEWER_LABELS = {
  CLOSE: 'viewer_close',
  NEXT: 'viewer_next',
  PREV: 'viewer_prev',
  ERROR: 'viewer_error',
  PLAY_START: 'viewer_play_start',
  PLAY_STOP: 'viewer_play_stop',
  FULL_SCREEN: 'viewer_full_screen',
  THUMBS: 'viewer_thumbs',
  DOWNLOAD: 'viewer_download',
  SHARE: 'viewer_share',
  ZOOM: 'viewer_zoom',
};
// Fancybox puts its labels into HTML; a text from the translation manager goes in as text.
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

export default {
  props: {
    config: { type: Object, required: true },
  },
  data() {
    const form = { viewing_skipped: false };
    FIELDS.forEach((field) => { form[field.name] = ''; });
    // The metadata id of the energy label the lookup found; shown, never typed.
    form.energy_label_id = '';
    form.viewing_slots = [this.newSlot()];

    return {
      step: 1,
      form,
      errors: {},
      photos: (this.config.photos || []).map((photo) => this.savedTile(photo, null)),
      photoError: '',
      tileSeq: 0,
      clientWarned: false,
      honeypot: '',
      sending: false,
      formError: '',
      done: false,
      doneTitle: '',
      doneMessage: '',
      doneBullets: [],
      donePreviewUrl: '',
      // The preview link of the concept: only from the answer of a step 1 save in this page, never from
      // the page itself (no token in the HTML); a picked-up page gets it again with the next save.
      propertyPreviewUrl: '',
      // The step 2 link, as slotsKey: what the server holds after the newest state save of this tab it
      // answered, and what the save on its way writes (null: none). Saves go one at a time (stateQueued:
      // another waits). Within this tab only: another tab of the same session can save other moments or
      // another step, and a save from either tab writes all its fields over the other; a save of this tab
      // that gave up at its 15 s limit can still be taken by the server later. This tab sees neither, and
      // coming back to it saves nothing.
      serverSlotsKey: '',
      flightSlotsKey: null,
      stateQueued: false,
      expired: false,
      stateTimer: null,
      // Address lookup: the add-house rules (addressEnrichmentAutofill.js). A value the guest typed
      // is not overwritten by the next address; a restored address is not looked up again.
      addressTouched: false,
      manualAddress: false,
      areaManuallyEdited: false,
      constructionYearManuallyEdited: false,
      areaFromLookup: false,
      energyStatus: '',
      energyToken: 0,
      // A resumed form whose address was complete: the lookup stays quiet until the guest edits it.
      restoredAddress: false,
      // "Opnieuw beginnen" is on its way.
      restarting: false,
      dragOver: false,
      // "Alle foto's verwijderen": the question is open; the one request is on its way.
      bulkAsking: false,
      bulkRemoving: false,
      dragDepth: 0,
      // A pick made by leaving the address fields is on its way (the typeahead's settling event).
      addressSettling: false,
      nextPressedAt: 0,
      saveAfterSettling: false,
      // The state save of a blur that started a pick waits for it (no half address is stored).
      saveStateAfterSettling: false,
      // The guest typed an addition while a pick was on its way; a pick without one keeps it, but only a
      // pick that started with the addition empty (a choice of the guest after typing one is theirs).
      additionTypedDuringPick: false,
      additionAtPickStart: null,
    };
  },
  computed: {
    t() {
      return this.config.texts || {};
    },
    stepLabels() {
      return [this.t.step_property, this.t.step_viewing_contact];
    },
    counterText() {
      return (this.t.step_counter || '').replace(':current', this.step).replace(':total', 2);
    },
    storageKey() {
      return 'vuuro-onboarding-b:' + this.config.submissionId;
    },
    uploading() {
      return this.photos.some((photo) => photo.status === 'uploading');
    },
    slotFields() {
      return SLOT_FIELDS;
    },
    hours() {
      return HOURS;
    },
    minutes() {
      return MINUTES;
    },
    quietLookup() {
      return this.restoredAddress && !this.addressTouched;
    },
    hasAddress() {
      return !!(this.form.street && this.form.city);
    },
    showPlaceFields() {
      return this.manualAddress || !!this.errors.street || !!this.errors.city;
    },
    summaryVisible() {
      return this.hasAddress || this.showPlaceFields || !!this.errors.area_m2 || !!this.errors.construction_year;
    },
    step1Sections() {
      const sections = [{ name: 'address', fields: this.fieldsOf('address') }];
      if (this.summaryVisible) {
        sections.push({ name: 'address-summary', fields: (this.showPlaceFields ? this.fieldsOf('place') : []).concat(this.fieldsOf('facts')) });
      }
      sections.push({ name: 'details', fields: this.fieldsOf('property') });
      return sections;
    },
    // "Teststraat 12A" / "1234 AB Teststad": a letter joins the number, another addition follows a dash.
    addressLines() {
      const form = this.form;
      const addition = form.house_number_addition || '';
      const number = form.house_number + (addition ? (/^[a-z]/i.test(addition) ? '' : '-') + addition : '');
      const compact = String(form.postcode || '').replace(/\s+/g, '').toUpperCase();
      const postcode = /^\d{4}[A-Z]{2}$/.test(compact) ? compact.slice(0, 4) + ' ' + compact.slice(4) : form.postcode;
      return [[form.street, number].filter(Boolean).join(' '), [postcode, form.city].filter(Boolean).join(' ')];
    },
    energyLabelText() {
      return (this.config.energyLabels || {})[this.form.energy_label_id] || '';
    },
    energyNote() {
      if (this.energyStatus === 'missing') return this.t.energy_label_missing;
      if (this.energyStatus === 'unavailable') return this.t.energy_label_unavailable;
      return '';
    },
    maxSlots() {
      return this.config.maxViewingSlots || 5;
    },
    activePhotoCount() {
      return this.photos.filter((photo) => photo.status !== 'failed').length;
    },
    deleting() {
      return this.photos.some((photo) => photo.removing);
    },
    // Saving step 1 waits for every photo action: an upload (also one waiting after a 429), a delete
    // or the remove-all.
    busy() {
      return this.sending || this.uploading || this.deleting || this.bulkAsking || this.bulkRemoving || this.restarting;
    },
    pickDisabled() {
      return this.sending || this.bulkRemoving;
    },
    canRemoveAll() {
      return this.photos.length > 0 && !this.busy;
    },
    // At least one moment with a date and a whole time that is still to come (the preview shows only those).
    hasPlannedSlot() {
      const now = Date.now();
      return this.form.viewing_slots.some((slot) => slot.date && /^\d{2}:\d{2}$/.test(slot.time || '')
        && new Date(slot.date + 'T' + slot.time).getTime() > now);
    },
    // What the preview shows of the moments: the step (the preview shows them only on step 2), the "no
    // moment" tick and each date and time.
    slotsKey() {
      return JSON.stringify([this.step, !!this.form.viewing_skipped, this.form.viewing_slots.map((slot) => [slot.date, slot.time])]);
    },
    // As far as this tab knows: the server took the moments on screen, and a save on its way writes the same.
    step2PreviewReady() {
      return !!this.propertyPreviewUrl && !this.form.viewing_skipped && this.hasPlannedSlot
        && this.serverSlotsKey === this.slotsKey && (this.flightSlotsKey === null || this.flightSlotsKey === this.slotsKey);
    },
  },
  watch: {
    form: { deep: true, handler() { this.cacheLocally(); this.scheduleState(); } },
    step() { this.cacheLocally(); this.saveStateNow(); },
  },
  created() {
    this.restore();
    FIELDS.concat(SLOT_FIELDS).forEach((field) => {
      if (field.name === 'available_from' || field.name === 'date') {
        field.min = this.config.today;
      }
    });
  },
  beforeDestroy() {
    clearTimeout(this.stateTimer);
    // The typeahead ends its wait as it goes, after this hook: a wizard that closes saves nothing for it.
    this.saveAfterSettling = false;
    this.saveStateAfterSettling = false;
    this.releasePhotos();
  },
  methods: {
    fieldsOf(group) {
      return FIELDS.filter((field) => field.group === group);
    },
    fieldsOfStep(step) {
      return FIELDS.filter((field) => STEP_GROUPS[step].includes(field.group));
    },
    fieldId(name) {
      return 'vuuro-onboarding-' + name;
    },
    errorId(name) {
      return 'vuuro-onboarding-' + name + '-error';
    },
    errorText(code) {
      return this.t[code] || this.t.invalid;
    },
    clearError(name) {
      if (this.errors[name]) {
        this.$delete(this.errors, name);
      }
      this.formError = '';
    },
    toggleSkipped() {
      this.clearSlotErrors();
      this.saveStateNow();
    },
    // Typing, not a fill by the lookup (a programmatic change fires no input event).
    onFieldInput(name) {
      this.clearError(name);
      // The guest types again: a save that waited for a pick is off; they click again when ready.
      this.saveAfterSettling = false;
      if (name === 'house_number_addition' && this.addressSettling) this.additionTypedDuringPick = true;
      if (LOOKUP_FIELDS.includes(name)) {
        this.addressTouched = true;
        // The label belonged to the previous address; a pending answer for it is dropped.
        this.energyToken += 1;
        this.energyStatus = '';
        this.form.energy_label_id = '';
      }
      if (name === 'area_m2') {
        this.areaManuallyEdited = true;
        this.areaFromLookup = false;
      }
      if (name === 'construction_year') this.constructionYearManuallyEdited = true;
    },
    // Leaving an address field can start a pick: the typeahead hears the focusout right after this blur,
    // so that save waits a tick and then, like every save, goes after the pick (saveStateNow).
    onFieldBlur(name) {
      if (!LOOKUP_FIELDS.includes(name)) {
        this.saveStateNow();
        return;
      }
      setTimeout(() => this.saveStateNow(), 0);
    },
    // Choosing a suggestion with the arrow keys and Enter in an address field counts as the guest's own choice.
    onFieldKeydown(name, event) {
      if (LOOKUP_FIELDS.includes(name) && ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) this.addressTouched = true;
    },
    chooseAddress() {
      this.addressTouched = true;
    },
    // The guest takes the address over by hand: a save that waited for a pick is off, and the lookup
    // drops that pick, also when focus had already left the address fields (after a press on
    // "Opslaan en verder") and so no focusout tells the lookup.
    onAddressTakeOver() {
      this.saveAfterSettling = false;
      [].concat(this.$refs.lookup || []).forEach((lookup) => lookup.dropPendingPick && lookup.dropPendingPick());
    },
    editAddress() {
      this.manualAddress = true;
      this.$nextTick(() => {
        const element = document.getElementById(this.fieldId('street'));
        if (element) element.focus();
      });
    },
    // ---- address lookup: postcode and house number to street, city, floor area and year built ----
    // A restored address is looked up again by the typeahead on its own; that answer is ignored until
    // the guest types in an address field or picks a suggestion, so a resume never overwrites what
    // the guest saved.
    lookupIgnored() {
      return !this.addressTouched && this.hasAddress;
    },
    // A pick that lands while step 1 is being saved, or after it, changes nothing.
    onAddressResolved(address) {
      if (this.lookupIgnored() || this.step !== 1 || this.sending) return;
      // A pick that started with the addition empty (a Tab from the house number) can land while the guest
      // is in the addition: what they type there, or typed while the pick was on its way, stays when the
      // address found has none. A pick the guest chose after typing an addition sets the address found.
      // The typeahead itself already drops a pick once the guest types in an address field; this keeps
      // the addition should a pick land anyway (the typeahead is shared with other forms).
      const typedAddition = String(this.form.house_number_addition || '');
      const keepAddition = !address.building_uid_appendix && typedAddition !== '' && this.additionAtPickStart === ''
        && (this.additionTypedDuringPick || this.isFieldFocused('house_number_addition'));
      this.additionTypedDuringPick = false;
      const manual = this.manualWithFocus();
      this.form.postcode = address.postcode || this.form.postcode;
      this.form.house_number = address.building_uid || this.form.house_number;
      this.form.house_number_addition = keepAddition ? typedAddition : (address.building_uid_appendix || '');
      this.form.street = address.street || this.form.street;
      this.form.city = address.location_name || this.form.city;
      ['postcode', 'house_number', 'house_number_addition', 'street', 'city'].forEach((name) => this.clearError(name));
      // The add-house rules: fill what the guest did not type, then start again from this address.
      // The area as a whole number (the helper's decimal comma would fail the integer field).
      const enrichment = address.enrichment || {};
      const area = Math.round(Number(enrichment.area));
      if (!this.areaManuallyEdited && enrichment.area != null && area >= 1 && area <= 999999) {
        this.form.area_m2 = String(area);
        this.areaFromLookup = true;
        this.clearError('area_m2');
      }
      const year = Number(enrichment.construction_year);
      if (!this.constructionYearManuallyEdited && enrichment.construction_year != null && year >= 1000 && year <= 2100) {
        this.form.construction_year = String(year);
        this.clearError('construction_year');
      }
      this.areaManuallyEdited = false;
      this.constructionYearManuallyEdited = false;
      this.lookupEnergyLabel(address);
      this.keepFocusFromManual(manual);
    },
    // "Enter it by hand" when it has the focus (a pick, a postcode answer or an unavailable lookup hides it).
    manualWithFocus() {
      const manual = this.$el.querySelector('.vuuro-onboarding__address-manual');
      return manual && document.activeElement === manual ? manual : null;
    },
    // The hidden button's focus moves on: to "change address" in the card, or to the street field when the
    // address is now typed by hand. Never to the page.
    keepFocusFromManual(manual) {
      if (!manual) return;
      this.$nextTick(() => {
        if (document.body.contains(manual)) return;
        const target = this.$el.querySelector('.vuuro-onboarding__address-head .vuuro-onboarding__address-link') || document.getElementById(this.fieldId('street'));
        if (target) target.focus();
      });
    },
    isFieldFocused(name) {
      return !!document.activeElement && document.activeElement.id === this.fieldId(name);
    },
    // Postcode only: street and city, no house number yet, so no floor area and no energy label.
    onPostcodeResolved(address) {
      if (this.lookupIgnored() || this.step !== 1 || this.sending) return;
      const manual = this.manualWithFocus();
      this.form.street = address.street || this.form.street;
      this.form.city = address.location_name || this.form.city;
      ['street', 'city'].forEach((name) => this.clearError(name));
      this.keepFocusFromManual(manual);
    },
    onAddressSettling(settling) {
      if (settling && !this.addressSettling) {
        this.additionTypedDuringPick = false;
        this.additionAtPickStart = String(this.form.house_number_addition || '');
      }
      this.addressSettling = settling;
      if (!settling && this.saveStateAfterSettling) {
        this.saveStateAfterSettling = false;
        this.saveStateNow();
      }
      // The press on "Opslaan en verder" left the address field and so started this pick.
      if (settling && this.step === 1 && Date.now() - this.nextPressedAt < 500) this.saveAfterSettling = true;
      this.maybeSaveAfterSettling();
    },
    // The press on "Opslaan en verder" already counts as the save: when a pick lands before the
    // release, the card grows, the button moves away under the pointer and no click follows. A tap
    // on a phone sends this mousedown too; a scroll that starts on the button does not.
    onNextPress() {
      this.nextPressedAt = Date.now();
      if (this.addressSettling) this.saveAfterSettling = true;
    },
    maybeSaveAfterSettling() {
      if (this.saveAfterSettling && !this.addressSettling && this.energyStatus !== 'loading' && !this.busy && !this.expired) {
        this.saveAfterSettling = false;
        this.$nextTick(() => this.saveProperty());
      }
    },
    onAddressUnavailable() {
      const manual = this.manualWithFocus();
      this.manualAddress = true;
      this.keepFocusFromManual(manual);
    },
    // One lookup per resolved address; only the answer for the newest address counts.
    async lookupEnergyLabel(address) {
      if (!this.config.urls.energy_label) return;
      this.energyToken += 1;
      const token = this.energyToken;
      this.form.energy_label_id = '';
      this.energyStatus = 'loading';
      try {
        const response = await this.client().post(this.config.urls.energy_label, {
          postcode: String(address.postcode || '').replace(/\s+/g, ''),
          huisnummer: address.building_uid,
          huisletter: address.huisletter || null,
          huisnummertoevoeging: address.huisnummertoevoeging || null,
          adresseerbaar_object_id: address.adresseerbaarobject_id || null,
        }, { timeout: 15000 });
        if (token !== this.energyToken) return;
        const data = response.data || {};
        const id = data.status === 'ok' && data.data ? String(data.data.metadata_id || '') : '';
        if (id && (this.config.energyLabels || {})[id]) {
          this.form.energy_label_id = id;
          this.energyStatus = 'ok';
        } else {
          this.energyStatus = data.status === 'empty' ? 'missing' : 'unavailable';
        }
      } catch (error) {
        if (token !== this.energyToken) return;
        this.energyStatus = 'unavailable';
      }
      this.maybeSaveAfterSettling();
    },
    // ---- viewing time: an hour list and a five-minute list, one "HH:MM" value ----
    timePart(time, index) {
      return String(time || '').split(':')[index] || '';
    },
    // An hour without minutes means the whole hour.
    setTime(slot, index, part, value) {
      const hour = part === 0 ? value : this.timePart(slot.time, 0);
      let minute = part === 1 ? value : this.timePart(slot.time, 1);
      if (part === 0 && hour && !minute) minute = '00';
      slot.time = hour || minute ? hour + ':' + minute : '';
      this.clearError(this.slotKey(index, 'time'));
    },
    // ---- viewing moments: one row per planned moment, keyed for the list, sent without the key ----
    newSlot(slot) {
      slotSeq += 1;
      const source = slot || {};
      return {
        key: 'slot-' + slotSeq,
        date: typeof source.date === 'string' ? source.date : '',
        time: typeof source.time === 'string' ? source.time : '',
        max_people: source.max_people != null && source.max_people !== '' ? String(source.max_people) : String(this.config.defaultMaxPeople || 10),
      };
    },
    slotKey(index, name) {
      return 'viewing_slots.' + index + '.' + name;
    },
    slotId(index, name) {
      return 'vuuro-onboarding-slot-' + index + '-' + name;
    },
    slotHeading(index) {
      return (this.t.viewing_slot_heading || '').replace(':n', index + 1);
    },
    slotsPayload() {
      return this.form.viewing_slots.map((slot) => ({ date: slot.date, time: slot.time, max_people: slot.max_people }));
    },
    clearSlotErrors() {
      const kept = {};
      Object.keys(this.errors).forEach((name) => {
        if (name !== 'viewing_slots' && name.indexOf('viewing_slots.') !== 0) kept[name] = this.errors[name];
      });
      this.errors = kept;
      this.formError = '';
    },
    // A sixth moment is not added; the guest sees the maximum only when trying.
    addSlot() {
      if (this.form.viewing_slots.length >= this.maxSlots) {
        this.errors = Object.assign({}, this.errors, { viewing_slots: 'viewing_slots_max' });
        return;
      }
      this.clearError('viewing_slots');
      this.form.viewing_slots.push(this.newSlot());
      const index = this.form.viewing_slots.length - 1;
      this.$nextTick(() => {
        const element = document.getElementById(this.slotId(index, 'date'));
        if (element) element.focus();
      });
    },
    // Rows after the removed one move up, so their errors would point at the wrong row: all go.
    removeSlot(index) {
      if (this.form.viewing_slots.length <= 1) return;
      this.form.viewing_slots.splice(index, 1);
      this.clearSlotErrors();
    },
    // The server checks the same (VuuroOnboardingBController::slotErrors), with the same codes.
    slotErrors() {
      const errors = {};
      const seen = {};
      const yearAhead = new Date();
      yearAhead.setFullYear(yearAhead.getFullYear() + 1);
      this.form.viewing_slots.forEach((slot, index) => {
        SLOT_FIELDS.forEach((field) => {
          const value = String(slot[field.name] == null ? '' : slot[field.name]).trim();
          const key = this.slotKey(index, field.name);
          if (value === '') {
            errors[key] = 'required';
          } else if (field.integer && (!/^\d+$/.test(value) || Number(value) < field.min || Number(value) > field.max)) {
            errors[key] = 'invalid';
          } else if (field.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            errors[key] = 'invalid';
          } else if (field.type === 'time-select') {
            // A time the lists cannot show (an hour outside them, a half-chosen time) shows as an
            // empty list, so it reads as missing; minutes off the five-minute steps get their own text.
            const [hour, minute] = value.split(':');
            if (!HOURS.includes(hour) || !/^\d{2}$/.test(minute || '')) errors[key] = 'required';
            else if (Number(minute) % 5 !== 0) errors[key] = 'viewing_time_step';
          }
        });
        const dateKey = this.slotKey(index, 'date');
        if (errors[dateKey] || errors[this.slotKey(index, 'time')]) return;
        const at = new Date(slot.date + 'T' + slot.time);
        const moment = slot.date + ' ' + slot.time;
        if (isNaN(at.getTime()) || at.getTime() <= Date.now()) errors[dateKey] = 'viewing_in_past';
        else if (at > yearAhead) errors[dateKey] = 'viewing_too_far';
        else if (seen[moment]) errors[dateKey] = 'viewing_slot_duplicate';
        else seen[moment] = true;
      });
      if (!this.form.viewing_slots.length) errors.viewing_slots = 'viewing_slots_required';
      return errors;
    },
    validateStep(step) {
      const errors = {};
      this.fieldsOfStep(step).forEach((field) => {
        const value = String(this.form[field.name] == null ? '' : this.form[field.name]).trim();
        if (value === '') {
          if (field.required) errors[field.name] = 'required';
          return;
        }
        if (field.integer) {
          const number = Number(value);
          if (!/^\d+$/.test(value) || number < field.min || number > field.max) errors[field.name] = 'invalid';
        }
        if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors[field.name] = 'invalid';
        if (field.type === 'tel' && !/^\+?[0-9 ()-]{6,30}$/.test(value)) errors[field.name] = 'invalid';
        if (field.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) errors[field.name] = 'invalid';
        if (field.type === 'time' && !/^\d{2}:\d{2}$/.test(value)) errors[field.name] = 'invalid';
      });
      if (step === 2 && !this.form.viewing_skipped) {
        Object.assign(errors, this.slotErrors());
      }
      const names = FIELDS.filter((field) => STEP_GROUPS[step].includes(field.group)).map((field) => field.name);
      const ofStep = (name) => names.includes(name) || (step === 2 && (name === 'viewing_slots' || name.indexOf('viewing_slots.') === 0));
      const kept = {};
      Object.keys(this.errors).forEach((name) => {
        if (!ofStep(name)) kept[name] = this.errors[name];
      });
      this.errors = Object.assign(kept, errors);
      return Object.keys(errors).length === 0;
    },
    goTo(step) {
      // A save that waited for a pick belongs to the step 1 the guest just left or came back to.
      this.saveAfterSettling = false;
      this.step = step === 2 ? 2 : 1;
      this.formError = '';
      this.$nextTick(() => {
        if (this.$refs.root && this.$refs.root.scrollIntoView) {
          this.$refs.root.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
        if (this.$refs.counter) this.$refs.counter.focus({ preventScroll: true });
      });
    },
    // Error key and element id of every field of a step, in the order they are on screen.
    errorTargets(step) {
      const targets = [];
      if (step === 2 && !this.form.viewing_skipped) {
        this.form.viewing_slots.forEach((slot, index) => {
          SLOT_FIELDS.forEach((field) => targets.push([this.slotKey(index, field.name), this.slotId(index, field.name)]));
        });
      }
      this.fieldsOfStep(step).forEach((field) => targets.push([field.name, this.fieldId(field.name)]));
      return targets;
    },
    focusFirstError() {
      this.$nextTick(() => {
        const first = this.errorTargets(this.step).find(([key]) => this.errors[key]);
        const element = first && document.getElementById(first[1]);
        if (element) element.focus();
      });
    },
    fieldValues(step) {
      const values = {};
      FIELDS.filter((field) => STEP_GROUPS[step].includes(field.group)).forEach((field) => { values[field.name] = this.form[field.name]; });
      return values;
    },
    // Step 1 "Next": the server creates or updates the concept property, then step 2 opens.
    async saveProperty() {
      if (this.busy || this.step !== 1) return;
      // A pick made by leaving the address fields (the click on this button did that), or the energy
      // label of the address, is still on its way: save once it landed, with what it brought.
      if (this.addressSettling || this.energyStatus === 'loading') {
        this.saveAfterSettling = true;
        return;
      }
      this.saveAfterSettling = false;
      if (!this.validateStep(1)) {
        this.focusFirstError();
        return;
      }
      this.sending = true;
      this.formError = '';
      try {
        const response = await this.client().post(this.config.urls.property, Object.assign(this.fieldValues(1), { energy_label_id: this.form.energy_label_id, submission_id: this.config.submissionId, website_url: this.honeypot }));
        if (response.data.done) {
          this.finish(response.data);
        } else {
          this.mergeSavedPhotos(response.data.photos || []);
          this.propertyPreviewUrl = this.linkUrl(response.data.preview_url);
          this.goTo(2);
        }
      } catch (error) {
        this.handleError(error);
      }
      this.sending = false;
    },
    async submit() {
      if (this.busy || this.step !== 2) return;
      if (!this.validateStep(2)) {
        this.focusFirstError();
        return;
      }
      this.sending = true;
      this.formError = '';
      try {
        const payload = Object.assign(this.fieldValues(2), {
          viewing_skipped: !!this.form.viewing_skipped,
          viewing_slots: this.form.viewing_skipped ? [] : this.slotsPayload(),
          submission_id: this.config.submissionId,
          website_url: this.honeypot,
        });
        const response = await this.client().post(this.config.urls.complete, payload);
        this.finish(response.data);
      } catch (error) {
        this.handleError(error);
      }
      this.sending = false;
    },
    finish(data) {
      clearTimeout(this.stateTimer);
      this.doneTitle = data.title || '';
      this.doneMessage = data.message || '';
      this.doneBullets = Array.isArray(data.bullets) ? data.bullets.filter((bullet) => typeof bullet === 'string' && bullet !== '') : [];
      this.donePreviewUrl = this.linkUrl(data.preview_url);
      this.done = true;
      this.forget();
      this.releasePhotos();
      this.$nextTick(() => { if (this.$refs.root && this.$refs.root.scrollIntoView) this.$refs.root.scrollIntoView({ block: 'start' }); });
    },
    // A link only to a web page: anything else in the field is no link.
    linkUrl(value) {
      return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '';
    },
    // Server texts are never shown as such: every state has its own text in the wizard.
    handleError(error) {
      const response = error && error.response;
      const status = response ? response.status : 0;
      const data = (response && response.data) || {};
      if (this.isExpired(error)) {
        this.expire();
      } else if (status === 422 && data.errors) {
        this.errors = Object.assign({}, this.errors, data.errors);
        if ((data.step || 1) !== this.step) this.goTo(data.step || 1);
        this.focusFirstError();
      } else if (status === 409 && data.error === 'busy') {
        this.formError = this.t.busy;
      } else if (status === 409 && data.error === 'property_missing') {
        this.goTo(1);
        this.$nextTick(() => { this.formError = this.t.property_missing; });
      } else if (status === 429) {
        // A new concept above the hourly ceiling per network has its own message; any other 429 is a throttle.
        this.formError = data.error === 'property_ceiling' && this.t.property_ceiling ? this.t.property_ceiling : this.t.too_many;
      } else if (status === 404) {
        this.formError = this.t.unavailable;
      } else {
        this.formError = this.t.error;
      }
    },
    // The shared client from resources/js/bootstrap.js, which carries the CSRF and X-Requested-With
    // headers. Without it every call fails into the normal error handling below.
    client() {
      if (window.axios) return window.axios;
      if (!this.clientWarned) {
        this.clientWarned = true;
        console.error('VuuroLandlordOnboardingWizard: window.axios is missing');
      }
      return { post: () => Promise.reject(new Error('no http client')), delete: () => Promise.reject(new Error('no http client')) };
    },
    // A tile from the server list: its thumbnail is the preview of this session before step 1 and
    // the public Photo thumbnail after it. Before step 1 a preview held in this tab (blob or data) is
    // kept, as it cannot go away like the temporary file does at step 1; a saved photo shows the small
    // public thumbnail and the copy in this tab is let go.
    savedTile(photo, local) {
      const kept = local && local.preview && /^(blob|data):/.test(local.preview) ? local.preview : null;
      // A small copy made in this tab, or the server's own preview (HEIC), stays after step 1: the public
      // thumbnail is only 200 px wide. The file itself held in this tab gives way to that thumbnail.
      const keepLocal = !!kept && (!!local.copied || kept.indexOf('data:') === 0 || !(photo.saved && photo.thumb));
      if (kept && !keepLocal) this.revoke(local);
      return {
        key: local ? local.key : 'saved-' + photo.token,
        token: photo.token,
        name: photo.name,
        preview: keepLocal ? kept : (photo.thumb || null),
        copied: keepLocal && !!local.copied,
        large: photo.large || null,
        status: 'saved',
        error: '',
        removing: false,
        progress: 100,
      };
    },
    // After step 1 the server list is the truth; local previews are kept, failed tiles dropped.
    mergeSavedPhotos(saved) {
      const local = {};
      this.photos.forEach((photo) => { if (photo.token) local[photo.token] = photo; });
      const keep = saved.map((photo) => photo.token);
      this.photos.filter((photo) => !photo.token || !keep.includes(photo.token)).forEach((photo) => this.revoke(photo));
      this.photos = saved.map((photo) => this.savedTile(photo, local[photo.token]));
    },
    // The file button and a drop on the photo section feed the same queue.
    addPhotos(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (this.pickDisabled) return undefined;
      return this.queuePhotoFiles(files);
    },
    // A counter of enter and leave events: WebKit gives no relatedTarget on dragleave, so a leave from
    // one child into another would otherwise switch the highlight off.
    onDragEnter(event) {
      if (this.pickDisabled || !this.hasFiles(event)) return;
      this.dragDepth += 1;
      this.dragOver = true;
    },
    onDragOver(event) {
      if (this.pickDisabled || !this.hasFiles(event)) return;
      this.dragOver = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragOver = false;
    },
    // The card takes every drop, so a photo or a link dropped next to the photo section does not open in
    // the browser, while dragged text still drops into a field. Moving on from the photo section ends its
    // highlight too: a leave event gets lost when a tile changes under the pointer.
    onCardDragOver(event) {
      if (!this.hasFiles(event)) {
        if (!this.isEditable(event.target)) event.preventDefault();
        return;
      }
      event.preventDefault();
      const section = this.$refs.photoSection;
      if (this.dragOver && !(section && section.contains(event.target))) {
        this.dragDepth = 0;
        this.dragOver = false;
      }
    },
    onCardDrop(event) {
      if (!this.hasFiles(event)) {
        if (!this.isEditable(event.target)) event.preventDefault();
        return;
      }
      event.preventDefault();
      this.dragDepth = 0;
      this.dragOver = false;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.dragOver = false;
      if (this.pickDisabled) return;
      const files = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
      if (files.length) this.queuePhotoFiles(files);
    },
    // A field that takes dropped text; a link dropped on a checkbox, a list or a date would open instead.
    isEditable(element) {
      return !!(element && element.closest && element.closest('textarea, [contenteditable="true"], input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input[type="url"], input[type="number"]'));
    },
    hasFiles(event) {
      const types = event.dataTransfer && event.dataTransfer.types;
      return !types || Array.from(types).includes('Files');
    },
    // Every chosen or dropped file gets a tile with its preview at once; uploads run three at a time,
    // each tile with its own progress. A drop skips the accept list of the file input, so the same
    // types are checked here (the server checks again). An iPhone photo (HEIC) is converted by the
    // server, which sends a preview back for browsers that cannot show HEIC themselves; every upload
    // also names the server copy (thumb), for the viewer and for a tile whose own preview does not show.
    async queuePhotoFiles(files) {
      this.photoError = '';
      const jobs = [];
      for (const file of files) {
        this.tileSeq += 1;
        const tile = { key: 'local-' + this.tileSeq, token: null, name: file.name, preview: null, previewing: false, copied: false, large: null, status: 'uploading', error: '', removing: false, progress: 0 };
        this.photos.push(tile);
        if (!this.isPhotoFile(file)) {
          this.failTile(tile, 'photo_wrong_type');
        } else if (file.size > this.config.maxFileBytes) {
          this.failTile(tile, 'photo_too_large');
        } else {
          // Only a file that is sent gets a copy: a wrong type or a file over the limit is not decoded.
          tile.previewing = true;
          this.queueTilePreview(tile, file);
          jobs.push({ tile, file });
        }
      }
      let next = 0;
      const lane = async () => {
        while (next < jobs.length && !this.expired) {
          const job = jobs[next];
          next += 1;
          await this.uploadPhoto(job.tile, job.file);
        }
      };
      await Promise.all(Array.from({ length: Math.min(PHOTO_UPLOADS_AT_ONCE, jobs.length) }, lane));
      // A save that waited while photos were uploading goes now.
      this.maybeSaveAfterSettling();
    },
    isPhotoFile(file) {
      const extension = String(file.name || '').split('.').pop().toLowerCase();
      return PHOTO_TYPES.includes(String(file.type || '').toLowerCase()) || PHOTO_EXTENSIONS.includes(extension);
    },
    async uploadPhoto(tile, file) {
      const data = new FormData();
      data.append('file', file);
      data.append('submission_id', this.config.submissionId);
      let paintedAt = 0;
      const onUploadProgress = (event) => {
        if (!event || !event.total) return;
        // 100 only once the server answered: the bytes are sent before the HEIC conversion runs.
        const progress = Math.min(99, Math.round((event.loaded * 100) / event.total));
        // A few repaints a second per tile; the first and 99 show at once.
        const now = Date.now();
        if (paintedAt && progress < 99 && now - paintedAt < PROGRESS_PAINT_MS) return;
        paintedAt = now;
        tile.progress = progress;
      };
      let response;
      for (let attempt = 0; ; attempt += 1) {
        try {
          response = await this.client().post(this.config.urls.photo, data, { onUploadProgress, timeout: PHOTO_UPLOAD_TIMEOUT_MS + Math.ceil(file.size / PHOTO_MIN_BYTES_PER_MS) });
          break;
        } catch (error) {
          // Too many uploads in this minute (a large drop): wait as long as the server asks and try
          // again, a few times, instead of failing every queued photo at once.
          const tooMany = error && error.response && error.response.status === 429;
          if (!tooMany || attempt >= PHOTO_RETRIES_ON_429 || this.expired) {
            this.uploadFailed(tile, error);
            return;
          }
          const wait = Number((error.response.headers || {})['retry-after']);
          tile.progress = 0;
          await new Promise((resolve) => setTimeout(resolve, Math.min(60, wait > 0 ? wait : 20) * 1000));
          if (this.expired) {
            this.failTile(tile, 'photo_failed');
            return;
          }
        }
      }
      try {
        tile.progress = 100;
        tile.token = response.data.token;
        tile.name = response.data.name || tile.name;
        tile.large = response.data.thumb || null;
        // The server's preview (HEIC) only where this tab made no copy of its own.
        if (response.data.preview && !tile.copied) {
          this.revoke(tile);
          tile.preview = response.data.preview;
        } else if (!tile.preview && !tile.previewing) {
          tile.preview = tile.large;
        }
        tile.status = 'done';
      } catch (error) {
        this.uploadFailed(tile, error);
      }
    },
    // An upload that failed, timed out (see PHOTO_UPLOAD_TIMEOUT_MS) or stayed throttled.
    uploadFailed(tile, error) {
      if (this.isExpired(error)) {
        this.expire();
        return;
      }
      const response = error && error.response;
      if (response && response.status === 429) {
        this.failTile(tile, 'too_many');
        this.photoError = this.t.too_many;
        return;
      }
      // The server's photo ceiling is there against abuse only, so it gets the general message, not a maximum.
      const code = response && response.data && response.data.error;
      this.failTile(tile, code && code !== 'photo_limit' && this.t[code] ? code : 'photo_failed');
    },
    failTile(tile, code) {
      tile.status = 'failed';
      tile.error = this.t[code] || this.t.photo_failed || '';
    },
    async removePhoto(photo) {
      if (photo.removing || photo.status === 'uploading' || this.sending || this.bulkRemoving) return;
      this.photoError = '';
      if (!photo.token) {
        this.dropTile(photo);
        return;
      }
      photo.removing = true;
      try {
        const response = await this.client().delete(this.config.urls.photo + '/' + encodeURIComponent(photo.token), { data: { submission_id: this.config.submissionId }, timeout: PHOTO_DELETE_TIMEOUT_MS });
        const data = (response && response.data) || {};
        // Only an answer that says the photo is gone (removed now, or already absent) takes the tile away.
        if (data.removed === true || data.absent === true) {
          this.dropTile(photo);
        } else {
          photo.removing = false;
          this.photoError = this.t.photo_remove_failed;
        }
      } catch (error) {
        photo.removing = false;
        if (this.isExpired(error)) return this.expire();
        const status = error && error.response && error.response.status;
        this.photoError = status === 429 ? this.t.too_many : this.t.photo_remove_failed;
      }
      this.maybeSaveAfterSettling();
      return undefined;
    },
    // "Alle foto's verwijderen": ask first (the product's sweetalert, else the browser's confirm). On yes
    // one request removes exactly the photos that were on screen when the guest confirmed. Not while
    // an upload, a save or a delete runs.
    removeAllPhotos() {
      if (!this.canRemoveAll) return Promise.resolve();
      const confirmed = this.photos.slice();
      const title = this.t.photos_remove_all_title;
      const text = String(this.t.photos_remove_all_text || '').replace(':count', String(confirmed.length));
      // Read when clicked: the page sets window.app.sweetalert only after the wizard mounted.
      const ask = window.app && typeof window.app.sweetalert === 'function'
        ? window.app.sweetalert({ title, text, buttons: [this.t.photos_remove_all_cancel, this.t.photos_remove_all_ok], dangerMode: true })
        : Promise.resolve(window.confirm(title + '\n' + text));
      this.bulkAsking = true;
      return Promise.resolve(ask).then((yes) => yes, () => false).then((yes) => {
        this.bulkAsking = false;
        if (!yes) {
          this.$nextTick(() => this.$refs.removeAll && this.$refs.removeAll.focus());
          // A save that waited while the question was open goes now.
          this.maybeSaveAfterSettling();
          return undefined;
        }
        return this.runRemoveAll(confirmed);
      });
    },
    async runRemoveAll(confirmed) {
      // The photos changed while the question was open (an upload started, a photo went): nothing happens.
      if (this.busy || confirmed.length !== this.photos.length || confirmed.some((photo, index) => this.photos[index] !== photo)) {
        this.photoError = this.t.photos_changed;
        return;
      }
      this.bulkRemoving = true;
      this.photoError = '';
      const tokens = confirmed.filter((photo) => photo.token).map((photo) => photo.token);
      try {
        if (tokens.length) {
          const response = await this.client().delete(this.config.urls.photos, { data: { submission_id: this.config.submissionId, tokens }, timeout: PHOTO_DELETE_TIMEOUT_MS });
          const data = (response && response.data) || {};
          const removed = Array.isArray(data.removed) ? data.removed : [];
          // Only an answer that names every confirmed photo as removed and an empty form clears the tiles.
          if (!Array.isArray(data.photos) || data.photos.length || !tokens.every((token) => removed.includes(token))) {
            // Photos whose cleanup failed are back in the form: show what the server holds, the others are gone.
            if (data.cleanup_failed > 0 && Array.isArray(data.photos)) this.showServerPhotos(data.photos);
            this.photoError = this.t.photos_remove_all_failed;
            return;
          }
        }
        // A tile without a token (a failed upload) never reached the server; it goes with the rest.
        confirmed.forEach((photo) => this.dropTile(photo));
        this.$nextTick(() => this.$refs.photoInput && this.$refs.photoInput.focus());
      } catch (error) {
        if (this.isExpired(error)) {
          this.expire();
          return;
        }
        const response = error && error.response;
        const data = (response && response.data) || {};
        if (response && response.status === 409 && data.error === 'photos_changed' && Array.isArray(data.photos)) {
          this.showServerPhotos(data.photos);
          this.photoError = this.t.photos_changed;
        } else {
          this.photoError = response && response.status === 429 ? this.t.too_many : this.t.photos_remove_all_failed;
        }
      } finally {
        this.bulkRemoving = false;
        // A save that waited while photos were being removed goes now, also after a refused answer.
        if (!this.expired) this.maybeSaveAfterSettling();
      }
    },
    // The photos the server holds (another tab changed them): its tiles, plus failed uploads of this tab.
    showServerPhotos(list) {
      const current = {};
      this.photos.forEach((photo) => { if (photo.token) current[photo.token] = photo; });
      const local = this.photos.filter((photo) => !photo.token);
      this.photos.filter((photo) => photo.token && !list.some((item) => item.token === photo.token)).forEach((photo) => this.revoke(photo));
      this.photos = list.map((item) => current[item.token] || this.savedTile(item, null)).concat(local);
    },
    dropTile(photo) {
      this.photos = this.photos.filter((item) => item.key !== photo.key);
      this.revoke(photo);
    },
    // A preview that does not show (a HEIC in Chrome, a file gone) falls back to the server copy once;
    // without one the tile says it has no preview, never that the photo is saved.
    previewFailed(photo) {
      // A copy that does not show is no copy: a preview the server sends later (HEIC) may take its place.
      photo.copied = false;
      if (photo.large && photo.preview !== photo.large) {
        this.revoke(photo);
        photo.preview = photo.large;
      } else {
        this.revoke(photo);
        photo.preview = null;
      }
    },
    canOpen(photo) {
      return !!photo.preview && (photo.status === 'done' || photo.status === 'saved');
    },
    // Enlarging uses the shared Fancybox helper of the property gallery, over every tile that shows
    // a photo, starting at the one clicked.
    openPhoto(photo) {
      const shown = this.photos.filter((item) => this.canOpen(item));
      const items = buildFancyboxItems(shown.map((item) => ({
        previewUrl: item.preview,
        thumbnailUrl: item.preview,
        fullUrl: item.large || item.preview,
        originalFilename: item.name,
      })));
      openPropertyGallery(items, shown.indexOf(photo), this.viewerOptions());
    },
    // The viewer in the language of the page, with the labels of the texts; the server always sends all
    // eleven (fallback texts), as Fancybox has no Dutch labels of its own.
    viewerOptions() {
      const lang = this.config.lang === 'en' ? 'en' : 'nl';
      const labels = {};
      Object.keys(VIEWER_LABELS).forEach((label) => {
        if (this.t[VIEWER_LABELS[label]]) labels[label] = escapeHtml(this.t[VIEWER_LABELS[label]]);
      });
      return { lang, i18n: { [lang]: labels } };
    },
    revoke(photo) {
      if (photo.preview && photo.preview.indexOf('blob:') === 0 && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(photo.preview);
      }
    },
    previewUrl(file) {
      try {
        return window.URL && window.URL.createObjectURL ? window.URL.createObjectURL(file) : null;
      } catch (error) {
        return null;
      }
    },
    // One photo at a time, so a drop of many does not decode them all at once.
    queueTilePreview(tile, file) {
      const run = () => this.makeTilePreview(tile, file);
      this.previewChain = (this.previewChain || Promise.resolve()).then(run, run);
    },
    // The small copy for the tile. It takes the place of a preview the server sent (HEIC, 320 px wide)
    // and of the file itself; where the browser cannot decode the file here (HEIC in Chrome) the file
    // itself, as before, unless the server's preview is already there. Nothing is decoded for a tile
    // that is gone or a wizard that closed. The tile is found by its key when the copy lands: the step 1
    // save replaces each tile by its saved one under the same key.
    makeTilePreview(tile, file) {
      const live = () => (this.photosClosed ? null : this.photos.find((photo) => photo.key === tile.key) || null);
      if (!live()) {
        tile.previewing = false;
        return Promise.resolve();
      }
      return smallPreviewBlob(file)
        .then((blob) => ({ url: this.previewUrl(blob), copy: true }), () => ({ url: this.previewUrl(file), copy: false }))
        .then(({ url, copy }) => {
          tile.previewing = false;
          const target = live();
          if (target) target.previewing = false;
          const serverPreview = !!target && !!target.preview && target.preview !== target.large;
          if (!target || !url || (!copy && serverPreview)) {
            if (url && url.indexOf('blob:') === 0 && window.URL.revokeObjectURL) window.URL.revokeObjectURL(url);
            if (target && !target.preview && target.large) target.preview = target.large;
            return;
          }
          this.revoke(target);
          target.preview = url;
          target.copied = copy;
        })
        .catch(() => { tile.previewing = false; });
    },
    // Every copy held for the tiles is let go, and copies still to come are not made or are let go.
    releasePhotos() {
      this.photosClosed = true;
      this.photos.forEach((photo) => this.revoke(photo));
    },
    isExpired(error) {
      const response = error && error.response;
      return !!response && (response.status === 419 || (response.status === 409 && response.data && response.data.error === 'session_expired'));
    },
    expire() {
      clearTimeout(this.stateTimer);
      this.forget();
      this.expired = true;
      this.releasePhotos();
    },
    // The expired panel: expire() already forgot the browser copy; the reload picks the form up again
    // through the resume cookie.
    reload() {
      window.location.reload();
    },
    // "Opnieuw beginnen" on a picked-up form: the server lets this browser go of the submission (the
    // office keeps the concept), then the page loads with an empty form.
    async startAgain() {
      if (this.busy || this.restarting || !this.config.urls.restart) return;
      this.restarting = true;
      this.formError = '';
      clearTimeout(this.stateTimer);
      try {
        await this.client().post(this.config.urls.restart, {});
        this.forget();
        this.reload();
      } catch (error) {
        this.restarting = false;
        const status = error && error.response && error.response.status;
        this.formError = status === 429 ? this.t.too_many : this.t.error;
        // The session normally stays: this tab saves its form again, so a save held back during the restart
        // is not lost (like any save it writes over another tab of the session). If the server did let the
        // session go and only the answer was lost, this save gets session_expired and the expired panel shows.
        this.saveStateNow();
      }
    },
    // The server keeps the wizard state of this session (step and values), so a reload or a second
    // tab opens where the guest was; saving is debounced while typing and immediate on blur.
    scheduleState() {
      clearTimeout(this.stateTimer);
      this.stateTimer = setTimeout(() => this.saveStateNow(), STATE_DELAY_MS);
    },
    async saveStateNow() {
      // A save that comes due while a pick is on its way (a debounce, a blur, a save that waited) goes after
      // the pick, with the address found: it would hold the address from before the pick.
      if (this.addressSettling) {
        clearTimeout(this.stateTimer);
        this.saveStateAfterSettling = true;
        return;
      }
      clearTimeout(this.stateTimer);
      // Once the guest starts again no save is asked for or sent (one already on its way still goes); a
      // restart that fails saves after all.
      if (this.done || this.expired || this.restarting || !this.config.urls.state) return;
      // One save at a time, so the server writes them in the order they were sent (unless one gave up at its
      // time limit and the server still takes it later); one asked for meanwhile goes after this one, with
      // the form as it is then.
      if (this.flightSlotsKey !== null) {
        this.stateQueued = true;
        return;
      }
      const payload = Object.assign({}, this.form, { viewing_slots: this.slotsPayload(), step: this.step, submission_id: this.config.submissionId, website_url: this.honeypot });
      const key = this.slotsKey;
      this.flightSlotsKey = key;
      try {
        // A save that hangs gives way after 15 seconds, so it never holds the link or the next save.
        const response = await this.client().post(this.config.urls.state, payload, { timeout: 15000 });
        const body = (response && response.data) || {};
        // The server holds these moments now; a form already sent shows its real moments instead.
        this.serverSlotsKey = body.ok === true && !body.done ? key : '';
      } catch (error) {
        // Not taken, or not known: what the server held stays only when this save carried the same moments.
        if (key !== this.serverSlotsKey) this.serverSlotsKey = '';
        if (this.isExpired(error)) this.expire();
        // Any other failure: the values are still on screen and in sessionStorage; the next save retries.
      } finally {
        this.flightSlotsKey = null;
      }
      if (this.stateQueued) {
        this.stateQueued = false;
        this.saveStateNow();
      }
    },
    // sessionStorage is only a cache for this tab, for when a state save did not get through.
    cacheLocally() {
      if (this.done) return;
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({ form: this.form }));
      } catch (error) {
        // Private mode or blocked storage: the server state still has the values.
      }
    },
    restore() {
      let cached = null;
      try {
        cached = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        cached = null;
      }
      const server = this.config.form || {};
      const cachedForm = (cached && cached.form) || {};
      const slots = [server.viewing_slots, cachedForm.viewing_slots].find((list) => Array.isArray(list) && list.length);
      if (slots) this.form.viewing_slots = slots.slice(0, this.maxSlots).map((slot) => this.newSlot(slot));
      Object.keys(this.form).forEach((name) => {
        if (name === 'viewing_slots') {
          return;
        } else if (name === 'viewing_skipped') {
          if (typeof server[name] === 'boolean') this.form[name] = server[name];
          else if (cached && cached.form && typeof cached.form[name] === 'boolean') this.form[name] = cached.form[name];
        } else if (typeof server[name] === 'string' && server[name] !== '') {
          this.form[name] = server[name];
        } else if (cached && cached.form && typeof cached.form[name] === 'string' && cached.form[name] !== '') {
          this.form[name] = cached.form[name];
        }
      });
      this.step = this.config.step === 2 && this.config.propertyId ? 2 : 1;
      this.restoredAddress = ['street', 'city', 'postcode', 'house_number'].every((name) => this.form[name] !== '');
    },
    forget() {
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // Storage blocked: nothing to remove.
      }
    },
  },
};
</script>

<style scoped>
/* One white card with a thin Vuuro green line on top; the title, a slim step bar and the fields share
   one surface, on a tight 4 / 8 / 12 / 16 / 24px rhythm, with the same light shadow at every width. The
   head (title and steps) carries a faint green wash that fades out, and the buttons sit in a rail at the bottom: on a phone that rail stays
   in view while the form scrolls, so the next step is always one tap away. */
.vuuro-onboarding {
  --vo-accent: #ff8212;
  --vo-brand: #afdf25; /* $brand-green */
  --vo-ink: #272728;
  --vo-muted: #6b6b70;
  --vo-line: #d9d9de;
  --vo-error: #c62828;
  background: #fff;
  border-radius: 12px;
  border-top: 4px solid var(--vo-brand);
  box-shadow: 0 0 0 1px rgba(39, 39, 40, 0.06), 0 4px 20px rgba(39, 39, 40, 0.08);
  color: var(--vo-ink);
  padding: 16px;
  position: relative;
  scroll-margin-top: 96px;
  width: 100%;
}
.vuuro-onboarding__head {
  background: linear-gradient(180deg, rgba(175, 223, 37, 0.14) 0%, rgba(175, 223, 37, 0) 100%);
  border-radius: 12px 12px 0 0;
  margin: -16px -16px 16px;
  padding: 16px 16px 12px;
}
@media (min-width: 768px) {
  .vuuro-onboarding { padding: 24px; }
  .vuuro-onboarding__head { margin: -24px -24px 16px; padding: 24px 24px 12px; }
  .vuuro-onboarding__title { font-size: 22px; }
}
.vuuro-onboarding__title { font-size: 20px; font-weight: 700; line-height: 1.25; margin: 0 0 12px; }
.vuuro-onboarding__subtitle { font-size: 16px; font-weight: 700; line-height: 1.3; margin: 0 0 8px; }
.vuuro-onboarding__text { margin: 0 0 12px; line-height: 1.45; }
.vuuro-onboarding__hint { color: var(--vo-muted); font-size: 13px; line-height: 1.4; margin: 4px 0 8px; }
.vuuro-onboarding__promise { margin: 8px 0 0; }
.vuuro-onboarding__contact-note { margin: 0 0 12px; }
.vuuro-onboarding__restart-row { margin: 4px 0 0; }
.vuuro-onboarding__restart { min-height: 44px; padding-left: 0; padding-right: 0; }
.vuuro-onboarding__block + .vuuro-onboarding__block { border-top: 1px solid var(--vo-line); margin-top: 16px; padding-top: 16px; }
.vuuro-onboarding__panel > :last-child { margin-bottom: 0; }
/* The thank-you points and the preview link: secondary, a new tab, its note below (16 Oct). */
.vuuro-onboarding__bullets { line-height: 1.45; list-style: disc; margin: 0 0 12px; max-width: 60ch; padding-left: 20px; }
.vuuro-onboarding__bullets li + li { margin-top: 6px; }
.vuuro-onboarding__preview { margin: 12px 0 0; }
.vuuro-onboarding__preview-link { align-items: center; color: var(--vo-accent); display: inline-flex; font-size: 14px; font-weight: 600; min-height: 44px; text-decoration: underline; }
/* The step 2 link without its address (no moment to come yet, or its save not in): the same words and box,
   muted, so nothing moves (19 Oct). */
.vuuro-onboarding__preview-wait { align-items: center; color: var(--vo-muted); cursor: default; display: inline-flex; font-size: 14px; font-weight: 600; min-height: 44px; }
.vuuro-onboarding__preview-note { color: var(--vo-muted); display: block; font-size: 13px; line-height: 1.4; }
/* The steps as dots on one line: (1) Je woning ---- (2) Bezichtiging en contact. The current step is an
   orange dot with its number and a bold label, a finished step a green dot with a check (the number stays
   for screen readers), a step still to come a grey ring; the line to a step turns green once the step
   before it is done. "Stap 1 van 2" and the step legend stay for screen readers (goTo() focuses the
   counter), hidden from view. */
.vuuro-onboarding__steps { align-items: center; display: flex; flex-wrap: nowrap; list-style: none; margin: 0; padding: 0; }
.vuuro-onboarding__step { align-items: center; color: var(--vo-muted); display: flex; flex: 0 0 auto; font-size: 13px; gap: 6px; line-height: 1.3; min-width: 0; }
.vuuro-onboarding__step + .vuuro-onboarding__step { flex: 1 1 auto; }
.vuuro-onboarding__step + .vuuro-onboarding__step::before { background: var(--vo-line); content: ''; flex: 1 1 16px; height: 2px; margin: 0 4px 0 8px; min-width: 16px; }
.vuuro-onboarding__step-nr {
  align-items: center; background: #fff; border: 2px solid var(--vo-line); border-radius: 50%; color: var(--vo-muted);
  display: inline-flex; flex: 0 0 24px; font-size: 12px; font-weight: 700; height: 24px; justify-content: center; width: 24px;
}
/* A label that does not fit is cut with an ellipsis, so the bar stays one line on the narrowest phone. */
.vuuro-onboarding__step-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vuuro-onboarding__step.is-active { color: var(--vo-ink); font-weight: 700; }
.vuuro-onboarding__step.is-active .vuuro-onboarding__step-nr { background: var(--vo-accent); border-color: var(--vo-accent); color: #fff; }
.vuuro-onboarding__step.is-active::before,
.vuuro-onboarding__step.is-done::before { background: var(--vo-brand); }
.vuuro-onboarding__step.is-done { color: var(--vo-ink); }
.vuuro-onboarding__step.is-done .vuuro-onboarding__step-nr { background: var(--vo-brand); border-color: var(--vo-brand); color: transparent; position: relative; }
.vuuro-onboarding__step.is-done .vuuro-onboarding__step-nr::after { color: var(--vo-ink); content: '\2713'; font-size: 13px; left: 0; position: absolute; right: 0; text-align: center; }
.vuuro-onboarding__counter { outline: none; }
.vuuro-onboarding__counter,
.vuuro-onboarding__legend {
  border: 0; clip: rect(0 0 0 0); clip-path: inset(50%); height: 1px; margin: -1px; overflow: hidden;
  padding: 0; position: absolute; white-space: nowrap; width: 1px;
}
.vuuro-onboarding__fieldset { border: 0; margin: 0; min-width: 0; padding: 0; }
.vuuro-onboarding__grid { display: grid; gap: 12px; grid-template-columns: minmax(0, 1fr); }
/* Two columns where the card is wide enough: a phone in landscape, and the half column from 1024px. */
@media (min-width: 480px) and (max-width: 767px), (min-width: 1024px) {
  .vuuro-onboarding__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .vuuro-onboarding__field--wide { grid-column: 1 / -1; }
}
/* A viewing moment (Mark, 15 Oct, S3): every label above its field, the fields on one bottom line. On a
   phone the date has its own row and time and maximum share one; where the card is wide enough all
   three share a row with fixed widths for time and maximum. */
.vuuro-onboarding__grid.vuuro-onboarding__grid--slot { align-items: end; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.vuuro-onboarding__grid--slot > .vuuro-onboarding__field:first-child { grid-column: 1 / -1; }
@media (min-width: 480px) and (max-width: 767px), (min-width: 1024px) {
  .vuuro-onboarding__grid.vuuro-onboarding__grid--slot { grid-template-columns: minmax(0, 1fr) 150px 100px; }
  .vuuro-onboarding__grid--slot > .vuuro-onboarding__field:first-child { grid-column: auto; }
}
/* On a desktop the maximum gets more room, so its label wraps less (16 Oct). */
@media (min-width: 1024px) {
  .vuuro-onboarding__grid.vuuro-onboarding__grid--slot { grid-template-columns: minmax(0, 1fr) 150px 140px; }
}
/* First and last name share a row at every width. */
.vuuro-onboarding__grid--pairs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.vuuro-onboarding__grid--pairs .vuuro-onboarding__field--wide { grid-column: 1 / -1; }
/* The time of a moment: an hour list and a minute list side by side. */
.vuuro-onboarding__time { align-items: center; display: flex; gap: 4px; }
.vuuro-onboarding__time .vuuro-onboarding__input { flex: 1 1 0; padding: 8px 22px 8px 8px; }
.vuuro-onboarding__time-sep { font-weight: 700; }
/* Step 1 address: postcode, house number and addition on one row at every width, the suggestions of
   the typeahead under it (44px rows on a phone), then the summary card with the address found, the
   floor area, the year built and the energy label (three columns where the card is wide enough), then
   the other fields. */
.vuuro-onboarding__address-hint { margin: 0 0 8px; }
.vuuro-onboarding__grid.vuuro-onboarding__grid--address { gap: 8px; grid-template-columns: minmax(0, 5fr) minmax(0, 4fr) minmax(0, 3fr); }
/* A card too narrow for three labels side by side (a small phone, the half column from 768px): the
   postcode on its own row, house number and addition under it. */
@media (max-width: 379px), (min-width: 768px) and (max-width: 899px) {
  .vuuro-onboarding__grid.vuuro-onboarding__grid--address { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .vuuro-onboarding__grid--address .vuuro-onboarding__field:first-child { grid-column: 1 / -1; }
}
.vuuro-onboarding >>> .theme-autocomplete__item { align-items: center; display: flex; min-height: 44px; }
.vuuro-onboarding__address-link {
  background: none; border: 0; color: var(--vo-accent); cursor: pointer; font-size: 13px; font-weight: 600;
  min-height: 44px; padding: 0; text-align: left; text-decoration: underline;
}
.vuuro-onboarding__address-summary { border: 1px solid var(--vo-line); border-radius: 8px; margin-top: 12px; padding: 12px; }
.vuuro-onboarding__address-title { font-size: 13px; font-weight: 700; line-height: 1.3; margin: 0 0 4px; }
.vuuro-onboarding__address-head { align-items: flex-start; display: flex; gap: 12px; justify-content: space-between; margin: 0 0 8px; }
.vuuro-onboarding__address-line { line-height: 1.4; margin: 0; min-width: 0; overflow-wrap: anywhere; padding-top: 12px; }
.vuuro-onboarding__address-head .vuuro-onboarding__address-link { flex: 0 0 auto; }
/* The card: floor area and year built narrow and side by side, the energy label wider (a row of its own
   on a phone), its note straight under it. Then the other fields in pairs: type and bedrooms, rent and
   available from, inputs level when a label wraps. */
.vuuro-onboarding__grid.vuuro-onboarding__grid--address-summary { gap: 8px 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.vuuro-onboarding__grid--address-summary .vuuro-onboarding__energy,
.vuuro-onboarding__grid--address-summary .vuuro-onboarding__area-hint,
.vuuro-onboarding__grid--address-summary .vuuro-onboarding__field--wide { grid-column: 1 / -1; }
.vuuro-onboarding__area-hint { margin: -4px 0 0; }
@media (min-width: 480px) and (max-width: 767px), (min-width: 1024px) {
  .vuuro-onboarding__grid.vuuro-onboarding__grid--address-summary { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr); }
  .vuuro-onboarding__grid--address-summary .vuuro-onboarding__energy { grid-column: auto; }
  /* Under the whole row: the energy label stays beside area and year. */
  .vuuro-onboarding__grid--address-summary .vuuro-onboarding__area-hint { order: 2; }
  .vuuro-onboarding__energy-value { min-height: 44px; }
}
.vuuro-onboarding__energy-value { align-items: center; display: flex; font-size: 16px; font-weight: 700; }
.vuuro-onboarding__energy-note { margin: 0; }
.vuuro-onboarding__details { margin-top: 16px; }
.vuuro-onboarding__grid.vuuro-onboarding__grid--details { align-items: end; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.vuuro-onboarding__field { display: flex; flex-direction: column; min-width: 0; }
.vuuro-onboarding__label { font-size: 13px; font-weight: 600; line-height: 1.3; margin: 0 0 4px; }
.vuuro-onboarding__input {
  background: #fff; border: 1px solid var(--vo-line); border-radius: 6px; color: var(--vo-ink);
  font-size: 16px; line-height: 1.25; min-height: 44px; min-width: 0; padding: 8px 12px; width: 100%;
}
.vuuro-onboarding__input::-webkit-date-and-time-value { text-align: left; }
.vuuro-onboarding__input:focus { border-color: var(--vo-accent); box-shadow: 0 0 0 3px rgba(255, 130, 18, 0.2); outline: none; }
/* A list looks and sizes like a text field in every engine (Safari keeps a native select at about 26px
   whatever its min-height), with a drawn arrow. */
select.vuuro-onboarding__input {
  -webkit-appearance: none; appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%236b6b70' stroke-width='2'/%3E%3C/svg%3E");
  background-position: right 8px center; background-repeat: no-repeat; background-size: 12px 8px; padding-right: 26px;
}
.has-error .vuuro-onboarding__input { border-color: var(--vo-error); }
.vuuro-onboarding__error { color: var(--vo-error); font-size: 13px; line-height: 1.35; margin: 4px 0 0; }
.vuuro-onboarding__error--form { margin-top: 12px; }
.vuuro-onboarding__check { align-items: flex-start; cursor: pointer; display: flex; gap: 10px; line-height: 1.4; margin: 0; min-height: 44px; padding: 8px 0; }
.vuuro-onboarding__check input { flex: 0 0 20px; height: 20px; margin: 0; width: 20px; }
/* A moment is a short field group; with one moment its "Moment 1 / remove" header is not shown (remove is
   disabled then anyway), from two moments each gets its header and a line between them. */
.vuuro-onboarding__slot { margin: 0 0 8px; }
.vuuro-onboarding__slot + .vuuro-onboarding__slot { border-top: 1px solid var(--vo-line); }
.vuuro-onboarding__slot:only-of-type .vuuro-onboarding__slot-head { display: none; }
.vuuro-onboarding__slot-head { align-items: center; display: flex; gap: 8px; justify-content: space-between; }
.vuuro-onboarding__slot-title { font-size: 13px; font-weight: 700; margin: 0; }
.vuuro-onboarding__slot-head .vuuro-onboarding__slot-remove { color: var(--vo-muted); flex: 0 0 auto; font-size: 13px; }
.vuuro-onboarding__slot-head .vuuro-onboarding__slot-remove:hover:not(:disabled) { color: var(--vo-error); }
/* Adding a moment is a clear secondary button: full width, dashed orange edge, a plus before the text. */
.vuuro-onboarding__slot-add { align-items: center; border: 1px dashed var(--vo-accent); border-radius: 6px; display: flex; justify-content: center; min-height: 44px; width: 100%; }
.vuuro-onboarding__slot-add::before { content: '+'; font-size: 18px; font-weight: 700; line-height: 1; margin-right: 6px; }
.vuuro-onboarding__or { align-items: center; color: var(--vo-muted); display: flex; font-size: 13px; gap: 12px; line-height: 1.3; margin: 12px 0 0; }
.vuuro-onboarding__or::before,
.vuuro-onboarding__or::after { border-top: 1px solid var(--vo-line); content: ''; flex: 1 1 0; }
.vuuro-onboarding__photos { border-radius: 8px; margin-top: 16px; outline: 2px dashed transparent; outline-offset: 4px; transition: outline-color 0.15s, background-color 0.15s; }
/* While files are dragged over the section: a dashed orange edge and a faint wash, no extra space. */
.vuuro-onboarding__photos.is-dragover { background-color: rgba(255, 130, 18, 0.1); outline-color: var(--vo-accent); }
.vuuro-onboarding__gallery { display: grid; gap: 8px; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); list-style: none; margin: 0 0 8px; padding: 0; }
.vuuro-onboarding__tile { border: 1px solid var(--vo-line); border-radius: 6px; display: flex; flex-direction: column; min-width: 0; overflow: hidden; }
.vuuro-onboarding__tile.is-failed { border-color: var(--vo-error); }
/* Square whatever the photo: overflow hidden stops a portrait photo from stretching the box, and
   the content fills it absolutely. */
.vuuro-onboarding__tile-media { aspect-ratio: 1 / 1; background: #f1f1f4; overflow: hidden; position: relative; }
.vuuro-onboarding__tile-media > .vuuro-onboarding__tile-img,
.vuuro-onboarding__tile-open,
.vuuro-onboarding__tile-empty { height: 100%; left: 0; position: absolute; top: 0; width: 100%; }
.vuuro-onboarding__tile-img { display: block; height: 100%; object-fit: cover; width: 100%; }
.vuuro-onboarding__tile-open { background: none; border: 0; cursor: zoom-in; display: block; padding: 0; }
.vuuro-onboarding__tile-open:focus { outline: none; }
.vuuro-onboarding__tile-open:focus-visible { outline: 3px solid var(--vo-accent); outline-offset: -3px; }
.vuuro-onboarding__tile.is-uploading .vuuro-onboarding__tile-img,
.vuuro-onboarding__tile.is-failed .vuuro-onboarding__tile-img { opacity: 0.55; }
.vuuro-onboarding__tile-empty { display: block; }
.vuuro-onboarding__tile-badge { background: rgba(39, 39, 40, 0.78); border-radius: 4px; bottom: 6px; color: #fff; font-size: 12px; left: 6px; line-height: 1.3; padding: 2px 6px; position: absolute; }
/* Upload progress of one tile: a thin bar under its picture. */
.vuuro-onboarding__tile-progress { background: var(--vo-line); display: block; height: 4px; overflow: hidden; }
.vuuro-onboarding__tile-progress-fill { background: var(--vo-accent); display: block; height: 100%; transition: width 0.2s ease-out; }
.vuuro-onboarding__tile-name { font-size: 12px; overflow: hidden; padding: 4px 8px 0; text-overflow: ellipsis; white-space: nowrap; }
.vuuro-onboarding__tile-error { color: var(--vo-error); font-size: 12px; line-height: 1.35; padding: 4px 8px 0; }
/* The X on a tile: a 44px hit area in the top right corner, a small round mark inside it. */
.vuuro-onboarding__tile-remove { align-items: flex-start; background: none; border: 0; cursor: pointer; display: flex; height: 44px; justify-content: flex-end; padding: 6px; position: absolute; right: 0; top: 0; width: 44px; z-index: 1; }
.vuuro-onboarding__tile-remove span { align-items: center; background: rgba(20, 20, 30, 0.72); border-radius: 50%; color: #fff; display: flex; font-size: 18px; height: 26px; justify-content: center; line-height: 1; width: 26px; }
.vuuro-onboarding__tile-remove:focus-visible { outline: none; }
.vuuro-onboarding__tile-remove:focus-visible span { box-shadow: 0 0 0 3px rgba(255, 130, 18, 0.6); }
.vuuro-onboarding__photos-head { align-items: center; display: flex; flex-wrap: wrap; gap: 0 12px; justify-content: space-between; }
.vuuro-onboarding__photos-head .vuuro-onboarding__label { margin-bottom: 0; }
.vuuro-onboarding__photos-remove-all { background: none; border: 0; color: var(--vo-error); cursor: pointer; font-size: 13px; min-height: 44px; padding: 0 4px; text-decoration: underline; }
.vuuro-onboarding__photo-remove { background: none; border: 0; color: var(--vo-error); cursor: pointer; font-size: 13px; min-height: 44px; padding: 0 8px; }
.vuuro-onboarding__dropzone { align-items: center; border: 2px dashed var(--vo-line); border-radius: 8px; color: var(--vo-ink); cursor: pointer; display: flex; flex-direction: column; gap: 2px; justify-content: center; min-height: 112px; padding: 16px; position: relative; text-align: center; transition: border-color 0.15s, background-color 0.15s; }
.vuuro-onboarding__photos.is-dragover .vuuro-onboarding__dropzone { background-color: rgba(255, 130, 18, 0.08); border-color: var(--vo-accent); }
/* Only where a pointer hovers: on a touch screen the colour stuck after a tap or a scroll over the zone. */
@media (hover: hover) {
  .vuuro-onboarding__dropzone:hover { background-color: rgba(255, 130, 18, 0.08); border-color: var(--vo-accent); }
}
.vuuro-onboarding__dropzone:focus-within { box-shadow: 0 0 0 3px rgba(255, 130, 18, 0.35); }
.vuuro-onboarding__dropzone.is-busy { cursor: progress; }
.vuuro-onboarding__dropzone.is-disabled { cursor: default; opacity: 0.6; }
.vuuro-onboarding__dropzone-icon { align-items: center; border: 2px solid var(--vo-accent); border-radius: 50%; color: var(--vo-accent); display: flex; font-size: 22px; font-weight: 700; height: 32px; justify-content: center; line-height: 1; margin-bottom: 4px; width: 32px; }
.vuuro-onboarding__dropzone-title { font-weight: 700; }
.vuuro-onboarding__dropzone-hint { color: var(--vo-muted); font-size: 13px; }
.vuuro-onboarding__dropzone-hint--touch { display: none; }
@media (hover: none) and (pointer: coarse) {
  .vuuro-onboarding__dropzone-hint--pointer { display: none; }
  .vuuro-onboarding__dropzone-hint--touch { display: inline; }
}
.vuuro-onboarding__file { height: 1px; opacity: 0; overflow: hidden; position: absolute; width: 1px; }
.vuuro-onboarding__honeypot { height: 1px; left: -10000px; overflow: hidden; position: absolute; width: 1px; }
/* The rail under the fields: a form error and the buttons. The step 1 promise sits under it, in the flow. */
.vuuro-onboarding__cta-rail { border-top: 1px solid var(--vo-line); margin-top: 20px; padding-top: 16px; }
.vuuro-onboarding__cta-rail .vuuro-onboarding__error--form { margin: 0 0 8px; }
/* Below 768px the rail sticks to the bottom of the screen while the form scrolls under it, fading the
   content out above the buttons, and keeps clear of the iPhone home bar. From 768px it stays in place. */
@media (max-width: 767px) {
  .vuuro-onboarding__cta-rail {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 22%);
    bottom: 0;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    position: sticky;
    z-index: 2;
  }
}
.vuuro-onboarding__nav { align-items: center; display: flex; gap: 8px; justify-content: space-between; }
/* The one filled element in the card, so the lead form has a single clear next step. */
.vuuro-onboarding__next { box-shadow: 0 6px 16px rgba(255, 130, 18, 0.32); flex: 1 1 auto; font-weight: 700; min-height: 52px; padding: 5px 16px; }
.vuuro-onboarding__back { color: var(--vo-muted); flex: 0 0 auto; min-height: 48px; padding: 0 12px; }
.vuuro-onboarding button:disabled { cursor: default; opacity: 0.6; }
</style>
